"use client";

import { useRouter } from "next/navigation";
import { useCallback } from "react";
import { useQueryParams } from "apps/website/hooks/useQueryParams";

import { getNextPageSlug } from "../navigation/getNextPageSlug";
import { getPreviousPageSlug } from "../navigation/getPreviousPageSlug";
import { SignupFlowSlug } from "../constants";

import { useFlowNavigationContext } from "./useFlowNavigationContext";

export interface FlowNavigation {
  goToNext: () => void;
  goBack: () => void;
  canContinue: boolean;
  canGoBack: boolean;
  nextPageSlug: SignupFlowSlug | null;
}

export function useFlowNavigation(currentSlug: string): FlowNavigation {
  const router = useRouter();
  const context = useFlowNavigationContext();
  const { chainQueryParamsToUrl } = useQueryParams();

  const nextPageSlug = getNextPageSlug(currentSlug, context);
  const previousPageSlug = getPreviousPageSlug(currentSlug, context);

  const canContinue = nextPageSlug !== null;
  const canGoBack = previousPageSlug !== null;

  const goToNext = useCallback(() => {
    if (canContinue && nextPageSlug) {
      const url = chainQueryParamsToUrl(nextPageSlug);
      router.push(url);
    }
  }, [ canContinue, nextPageSlug, router, chainQueryParamsToUrl ]);

  const goBack = useCallback(() => {
    if (canGoBack && previousPageSlug) {
      const url = chainQueryParamsToUrl(previousPageSlug);
      router.push(url);
    }
  }, [ canGoBack, previousPageSlug, router, chainQueryParamsToUrl ]);

  return {
    goToNext,
    goBack,
    canContinue,
    canGoBack,
    nextPageSlug,
  };
}
